import { Reveal, Eyebrow } from "./Reveal";

const PANELS = [
  {
    id: "acquisition",
    n: "01",
    title: "Property Acquisition",
    text: "We work directly with property owners who may be considering selling residential properties, land, multifamily properties, or other real estate.",
    img: "/images/acquisition-pnw-home.webp",
    alt: "Pacific Northwest craftsman home with warm windows among evergreen trees after rain",
  },
  {
    id: "land-research",
    n: "02",
    title: "Land & Development",
    text: "We evaluate parcels for redevelopment potential, zoning considerations, access, surrounding development, lot configuration, and other factors that may affect future use.",
    img: "/images/land-subdivision-lake.webp",
    alt: "Quiet subdivision and open lots overlooking a misty Pacific Northwest lake",
  },
  {
    id: "assemblages",
    n: "03",
    title: "Property Assemblages",
    text: "Sometimes several neighboring properties may collectively create an opportunity that does not exist individually. We research these situations and communicate directly with owners when appropriate.",
    img: "/images/neighborhood-aerial.webp",
    alt: "Aerial view of a misty suburban neighborhood by the water",
  },
  {
    id: "intelligence",
    n: "04",
    title: "Property Intelligence",
    text: "Maps, title research, zoning, ownership history, and development potential. We study the public record before we ever start a conversation, so discussions begin from understanding.",
    img: "/images/research-planning-studio.webp",
    alt: "Maps and zoning documents in a lakeside planning studio on a rainy day",
  },
  {
    id: "conversations",
    n: "05",
    title: "Owner Conversations",
    text: "Conversations about a home, family property, or long-held land are personal. We explain what we see, listen first, and share information openly — whether or not it ever involves us. We also welcome conversations with investors, builders, brokers, and other professionals when opportunities align.",
    img: "/images/owner-conversation-plans.webp",
    alt: "Two people reviewing architectural plans together in a warm studio",
  },
];

const ParcelMap = () => (
  <svg viewBox="0 0 480 320" className="w-full h-full object-cover" role="img" aria-label="Abstract parcel and survey map illustration">
    <rect width="480" height="320" fill="#18352C" />
    <g stroke="#D9D6CE" strokeOpacity="0.22" strokeWidth="1" fill="none">
      <rect x="32" y="36" width="120" height="90" />
      <rect x="152" y="36" width="96" height="90" />
      <rect x="248" y="36" width="200" height="130" />
      <rect x="32" y="126" width="84" height="110" />
      <rect x="116" y="126" width="132" height="110" />
      <rect x="32" y="236" width="170" height="56" />
      <rect x="202" y="236" width="120" height="56" />
      <rect x="322" y="166" width="126" height="126" />
      <rect x="248" y="166" width="74" height="70" />
    </g>
    <rect x="116" y="126" width="132" height="110" fill="#A77A50" fillOpacity="0.1" stroke="#A77A50" strokeWidth="1.5" />
    <circle cx="182" cy="181" r="5" fill="#A77A50" />
    <text x="34" y="308" fill="#718477" fontSize="10" letterSpacing="3" fontFamily="Manrope, sans-serif">
      PARCEL STUDY · GREATER SEATTLE
    </text>
  </svg>
);

const AssemblageMap = () => (
  <svg viewBox="0 0 480 320" className="w-full h-full object-cover" role="img" aria-label="Abstract map of adjacent parcels forming an assemblage">
    <rect width="480" height="320" fill="#18352C" />
    <g stroke="#D9D6CE" strokeOpacity="0.22" strokeWidth="1" fill="none">
      <rect x="32" y="36" width="104" height="86" />
      <rect x="136" y="36" width="104" height="86" />
      <rect x="240" y="36" width="104" height="86" />
      <rect x="344" y="36" width="104" height="86" />
      <rect x="32" y="122" width="104" height="86" />
      <rect x="136" y="122" width="104" height="86" />
      <rect x="240" y="122" width="104" height="86" />
      <rect x="344" y="122" width="104" height="86" />
      <rect x="32" y="208" width="208" height="76" />
      <rect x="240" y="208" width="208" height="76" />
    </g>
    <rect x="136" y="36" width="104" height="86" fill="#A77A50" fillOpacity="0.1" stroke="#A77A50" strokeWidth="1.5" />
    <rect x="240" y="36" width="104" height="86" fill="#A77A50" fillOpacity="0.1" stroke="#A77A50" strokeWidth="1.5" />
    <rect x="240" y="122" width="104" height="86" fill="#A77A50" fillOpacity="0.1" stroke="#A77A50" strokeWidth="1.5" />
    <line x1="240" y1="36" x2="240" y2="208" stroke="#A77A50" strokeWidth="2.5" />
    <circle cx="240" cy="122" r="5" fill="#A77A50" />
    <text x="34" y="308" fill="#718477" fontSize="10" letterSpacing="3" fontFamily="Manrope, sans-serif">
      ASSEMBLAGE STUDY · ADJACENT PARCELS
    </text>
  </svg>
);

export const WhatWeDo = () => {
  return (
    <section id="what-we-do" data-testid="what-we-do-section" className="bg-white py-24 sm:py-32 lg:py-40 scroll-mt-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal className="max-w-3xl">
          <Eyebrow>What We Do</Eyebrow>
          <h2 className="mt-5 font-display text-4xl sm:text-5xl text-[#111513] leading-[1.12] text-balance">
            Understanding the property — <span className="italic text-[#A77A50]">and the possibilities.</span>
          </h2>
          <p className="mt-6 text-lg text-[#5C665F] leading-relaxed">
            Every owner&rsquo;s situation is different. Our work brings together property research, direct
            conversations, and a long-term interest in thoughtful housing opportunities.
          </p>
        </Reveal>
        <div className="mt-20 sm:mt-28 space-y-24 sm:space-y-32">
          {PANELS.map((p, i) => {
            const flip = i % 2 === 1;
            return (
              <Reveal key={p.id} className="grid lg:grid-cols-12 gap-8 lg:gap-14 items-center">
                <div className={`lg:col-span-5 ${flip ? "lg:order-2" : ""}`}>
                  <span className="font-display text-6xl sm:text-7xl text-[#A77A50]" aria-hidden="true">{p.n}</span>
                  <h3 className="mt-5 font-display text-3xl sm:text-4xl text-[#111513] leading-tight">{p.title}</h3>
                  <div className="mt-5 w-12 h-px bg-[#A77A50]" aria-hidden="true" />
                  <p className="mt-5 text-base sm:text-lg text-[#5C665F] leading-relaxed">{p.text}</p>
                </div>
                <div className={`lg:col-span-7 ${flip ? "lg:order-1" : ""}`} data-testid={`service-card-${p.id}`}>
                  <div className="overflow-hidden">
                    {p.svg ? (
                      <div className="aspect-[16/10]">{p.svg === "assemblage" ? <AssemblageMap /> : <ParcelMap />}</div>
                    ) : (
                      <img
                        src={p.img}
                        alt={p.alt}
                        loading="lazy"
                        className="w-full aspect-[16/10] object-cover transition-transform duration-[1200ms] ease-out hover:scale-[1.025]"
                      />
                    )}
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};
